/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { first, map } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import { AuthorizeService } from '@platform/auth/authorize.service';
import * as PlatformActions from '@platform/platform.actions';
import { selectUser } from '@platform/platform.selectors';
import { CurrentUser } from '@platform/platform.state';
import { go } from '@platform/router.actions';
import { RouteGuard } from '@platform/routing/route.guard';
import { ResourceState, ResourceStatuses } from '@platform/shared/resource.state';
import * as CoreActions from '@core/core.actions';
import { selectAllCustomerSpaces } from '@core/core.selectors';
import { CustomerSpace } from '@core/core.state';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomerSpaceListGuard implements RouteGuard {
  constructor(
    private store$: Store<RootState>,
    private authorizeService: AuthorizeService,
  ) {}

  private static getValues(customerSpace: CustomerSpace): Map<string, string> {
    const values = new Map<string, string>();
    values.set('customerSpaceId', customerSpace.id);

    return values;
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return await this.preloadOrRedirect();
  }

  async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return await this.preloadOrRedirect();
  }

  private async preloadOrRedirect(): Promise<boolean> {
    const user = await this.getUser();
    const customerSpacesState = await this.loadAndWaitForCustomerSpaces();

    const canActivate = customerSpacesState.status === ResourceStatuses.SUCCEEDED;
    if (
      canActivate &&
      !user.isInternal &&
      customerSpacesState.data.length === 1 &&
      (await firstValueFrom(
        this.authorizeService.isAuthorized('COCKPIT_READ', CustomerSpaceListGuard.getValues(customerSpacesState.data[0])),
      ))
    ) {
      this.store$.dispatch(go({ path: ['customer-spaces', customerSpacesState.data[0].id, 'cockpit'] }));
    }

    if (!canActivate) {
      this.store$.dispatch(go({ path: ['error'] }));
    }

    return canActivate;
  }

  private async loadAndWaitForCustomerSpaces(): Promise<ResourceState<CustomerSpace[]>> {
    this.store$.dispatch(CoreActions.loadCustomerSpaces());

    return await firstValueFrom(
      this.store$.pipe(
        select(selectAllCustomerSpaces),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
      ),
    );
  }

  private async getUser(): Promise<CurrentUser> {
    this.store$.dispatch(PlatformActions.loadCurrentUser());

    return await firstValueFrom(
      this.store$.pipe(
        select(selectUser),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
        map((state) => state.data),
      ),
    );
  }
}
